import { normalize } from "normalizr";
import { all, call, put, takeLatest } from "redux-saga/effects";

import {
    TOPIC_INFO_FETCH_TRIGGER,
    TOPIC_INVITE_MODERATOR_TRIGGER,
    TOPIC_REMOVE_MODERATOR_TRIGGER,
    TopicInfoFetchTriggerAction,
    TopicInviteModeratorTriggerAction,
    TopicRemoveModeratorTriggerAction,
    topicInfoFetchApi,
    topicInviteModeratorApi,
    topicRemoveModeratorApi,
} from "@/mk2/apps/forum/containers/TopicInfo/TopicInfo.actions";
import {
    TopicEntity,
    TopicModeratorsDetailsEntity,
    TopicModeratorsDetailsSchema,
    TopicSchema,
} from "@/mk2/apps/forum/schemas";
import { XHRAction, XHRPostError, handleXHRPostErrorSaga, normalizeError } from "@/mk2/helpers/api";
import { getLogger } from "@/mk2/logger";
import { FORUM_TOPIC_EDIT_USERNAME_YOU_WANNA_ADD } from "@/mk/autogenerated/translation";
import {
    forum_api_get_topic_info_url,
    forum_api_topic_moderator_invite_url,
    forum_api_topic_moderator_remove_url,
} from "@/mk/urls/functions";

const logger = getLogger("forum/TopicInfo.sagas");

interface ApiResponseTopicInfo {
    body: {
        topic: TopicEntity;
        moderatorsDetails: TopicModeratorsDetailsEntity;
    };
}

function* fetchTopicInfo({ topicSlug, xhr }: TopicInfoFetchTriggerAction & XHRAction) {
    yield put(topicInfoFetchApi.request(topicSlug));

    try {
        const response: ApiResponseTopicInfo = yield call(() =>
            xhr.get(forum_api_get_topic_info_url(), { topicSlug }),
        );

        const normalizedResponse = normalize(response.body, {
            topic: TopicSchema,
            moderatorsDetails: TopicModeratorsDetailsSchema,
        });
        yield put(topicInfoFetchApi.success(topicSlug, normalizedResponse));
    } catch (error) {
        logger.error(error);
        yield put(topicInfoFetchApi.failure(topicSlug, normalizeError(error)));
    }
}

interface ApiResponseInviteModerator {
    body: {
        topic: TopicEntity;
        moderatorsDetails: TopicModeratorsDetailsEntity;
    };
}

function* inviteModerator({ topicSlug, xhr }: TopicInviteModeratorTriggerAction & XHRAction) {
    // CeMi: toto bude neskor zamenene za screen so searchom kde sa vybera uzivatel
    const username: string = prompt(FORUM_TOPIC_EDIT_USERNAME_YOU_WANNA_ADD);
    if (!username) {
        return;
    }
    yield put(topicInviteModeratorApi.request(topicSlug, username));

    try {
        const response: ApiResponseInviteModerator = yield call(() =>
            xhr.post(forum_api_topic_moderator_invite_url(), { topicSlug, username }),
        );

        const normalizedResponse = normalize(response.body, {
            topic: TopicSchema,
            moderatorsDetails: TopicModeratorsDetailsSchema,
        });
        yield put(topicInviteModeratorApi.success(topicSlug, username, normalizedResponse));
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(topicInviteModeratorApi.failure(topicSlug, username, normalizeError(error)));
    }
}

interface ApiResponseRemoveModerator {
    body: {
        topic: TopicEntity;
        moderatorsDetails: TopicModeratorsDetailsEntity;
    };
}

function* removeModerator({
    topicSlug,
    username,
    xhr,
}: TopicRemoveModeratorTriggerAction & XHRAction) {
    yield put(topicRemoveModeratorApi.request(topicSlug, username));

    try {
        const response: ApiResponseRemoveModerator = yield call(() =>
            xhr.post(forum_api_topic_moderator_remove_url(), { topicSlug, username }),
        );

        const normalizedResponse = normalize(response.body, {
            topic: TopicSchema,
            moderatorsDetails: TopicModeratorsDetailsSchema,
        });
        yield put(topicRemoveModeratorApi.success(topicSlug, username, normalizedResponse));
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(topicRemoveModeratorApi.failure(topicSlug, username, normalizeError(error)));
    }
}

export default function* root() {
    yield all([
        takeLatest(TOPIC_INFO_FETCH_TRIGGER, fetchTopicInfo),
        takeLatest(TOPIC_INVITE_MODERATOR_TRIGGER, inviteModerator),
        takeLatest(TOPIC_REMOVE_MODERATOR_TRIGGER, removeModerator),
    ]);
}
